<template>
  <div class="flex flex-col bg-[#f8fafc] gap-6 overflow-hidden text-left">
    <div class="flex-1 bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden border-t-4 border-t-[#0d4169] flex flex-col">
      <div class="px-6 pt-5 pb-1 flex justify-between items-center shrink-0">
        <h2 class="text-base font-bold text-[#0d4169] text-left">Product</h2>
      </div>

      <!-- Filters -->
      <form class="px-6 pb-5 pt-2 shrink-0" @submit.prevent>
        <div class="flex flex-wrap items-center gap-x-12 gap-y-4 text-left">
          <div class="flex items-center gap-3 min-w-[240px]">
            <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left shrink-0">Calendar:</label>
            <input
              v-model="currentDate"
              @change="loadProducts"
              type="date"
              @keydown.prevent
              class="flex-1 px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer"
            >
          </div>
          <div class="flex items-center gap-3 min-w-[280px]">
            <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left shrink-0">Department:</label>
            <select
              v-model="selectedDepartment"
              @change="loadProducts"
              class="flex-1 px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer"
            >
              <option value="" disabled selected>Select Department</option>
              <option v-for="dept in filteredDepartments" :key="dept.id" :value="dept.id">{{ dept.name }}</option>
            </select>
          </div>
        </div>
      </form>

      <!-- Table -->
      <div class="px-6 pb-6 overflow-auto flex-1 custom-scrollbar">
        <div v-if="loading" class="flex items-center justify-center py-16">
          <div class="h-8 w-8 border-4 border-gray-300 border-t-transparent rounded-full animate-spin"></div>
        </div>
        <template v-else>
        <table class="w-2/3 daily-product-table">
          <thead>
            <tr>
              <th class="w-16 text-center bg-[#f1f5f9]">No.</th>
              <th class="min-w-[200px] bg-[#f1f5f9]">Product Name</th>
              <th class="w-32 bg-[#f1f5f9] text-center">U.O.M</th>
              <th class="w-32 bg-[#f1f5f9] text-center">Total Produced</th>
              <th class="w-32 bg-[#f1f5f9] text-center">Quantity</th>
              <th v-if="!isFinalized && !isFutureDateSelected()" class="w-24 bg-[#f1f5f9] text-center">
                <button
                  type="button"
                  @click="savePartialBatch"
                  :disabled="!canPartialSaveBatch"
                  class="px-2.5 py-0.5 bg-[#0d4169] text-white text-xs font-bold rounded hover:bg-[#0a3354] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span v-if="isPartialSaving" class="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  <span v-else>Save</span>
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in products" :key="index" class="hover:bg-slate-50 transition-colors group">
              <td class="text-center text-slate-500">{{ index + 1 }}</td>
              <td class="">{{ row.name || row.productName }}</td>
              <td class="text-center">{{ row.uom }}</td>
              <td class="text-center text-slate-700 font-medium">{{ formatNumber(row.totalProduced) }}</td>
              <td class="text-center">
                <input
                  v-model="row.quantity"
                  type="number"
                  min="0"
                  step="any"
                  inputmode="decimal"
                  placeholder="0"
                  :disabled="inventoryFieldDisabled(row, 'quantity')"
                  @input="onQuantityInput(row)"
                  @blur="sanitizeQuantity(row)"
                  :class="inventoryFieldDisabled(row, 'quantity') ? 'bg-slate-100 opacity-60 cursor-not-allowed' : 'bg-red-50 focus:bg-white'"
                  class="w-[100px] mx-auto px-1 py-0.5 border border-transparent hover:border-[#cbd5e1] focus:border-[#0d4169] rounded outline-none transition-all text-sm text-center"
                >
              </td>
              <td v-if="!isFinalized && !isFutureDateSelected()" class="text-center"></td>
            </tr>
          </tbody>
        </table>
        <div class="flex justify-center mt-6">
          <button
            @click="openSaveModal"
            :disabled="!canSave || isSaving"
            class="px-6 py-1.5 bg-[#0d4169] text-white text-sm font-bold rounded-md hover:bg-[#0a3354] transition-all active:scale-95 shadow-md flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            <span v-if="isSaving" class="spinner"></span>
            {{ isSaving ? 'Saving...' : 'Save' }}
          </button>
        </div>
        </template>
      </div>
    </div>

    <!-- Save confirmation modal -->
    <div v-if="saveModal.open" class="fixed inset-0 z-[200] flex items-start justify-center px-4 py-8 overflow-y-auto bg-black/50 backdrop-blur-sm" @click.self="closeSaveModal">
      <div class="bg-white rounded-xl shadow-2xl w-full max-w-md transform transition-all animate-in fade-in zoom-in duration-300 text-left relative overflow-hidden">
        <div class="px-6 py-4 bg-[#0d4169] text-white flex items-center justify-between rounded-t-xl">
          <div class="text-left">
            <h3 class="text-base font-bold m-0">Confirm Save product</h3>
          </div>
          <button type="button" class="p-1 hover:bg-white/10 rounded-full transition-colors text-white" @click="closeSaveModal" aria-label="Close save modal">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <div class="p-5 space-y-4">
          <div class="text-xs font-semibold text-slate-500">Please enter the code below to confirm that you want to save.</div>

          <div class="px-3 py-2 rounded-md border border-slate-200 bg-green-100 text-[#0d4169] text-sm font-bold tracking-[0.25em] text-center">
            {{ saveModal.code }}
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Enter Code</label>
            <input
              v-model.trim="saveModal.input"
              type="text"
              maxlength="4"
              class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm uppercase"
            >
          </div>
          <div v-if="saveModal.error" class="text-xs text-red-600 font-semibold">{{ saveModal.error }}</div>
        </div>

        <div class="px-5 py-4 flex items-center justify-end gap-2">
          <button type="button" @click="closeSaveModal" class="px-4 py-2 text-sm font-semibold text-slate-500 hover:text-slate-800 transition-colors">Cancel</button>
          <button
            type="button"
            @click="confirmSave"
            :disabled="isSaving"
            class="px-5 py-2 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all disabled:opacity-60"
          >
            {{ isSaving ? 'Saving...' : 'Confirm' }}
          </button>
        </div>
      </div>
    </div>

  </div>

  <!-- Toast Notification -->
  <Transition name="toast">
    <div v-if="toast.show" class="fixed top-4 right-4 z-[1000] max-w-sm">
      <div
        :class="[
          'px-6 py-3 rounded-lg shadow-2xl text-white flex items-center gap-3 min-w-[300px]',
          toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'
        ]"
      >
        <span v-if="toast.type === 'success'">✅</span>
        <span v-else>❌</span>
        <span class="font-medium">{{ toast.message }}</span>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import { apiFetch } from '../../../utils/api'
import store from '../../../store'

const selectedDepartment = ref('')
const currentDate = ref('')
const todayDate = ref(new Date().toISOString().split('T')[0])
const departments = ref([])
const products = ref([])
const loading = ref(false)
const isSaving = ref(false)
const isPartialSaving = ref(false)
const isSnapshotLocked = ref(false)
const isFinalized = ref(false)
const recipeSnapshotsLoading = ref(false)
const toast = ref({ show: false, message: '', type: 'success' })
let toastTimer = null

function showToast(message, type = 'success', duration = 3000) {
  if (toastTimer) clearTimeout(toastTimer)
  toast.value = { show: true, message, type }
  toastTimer = setTimeout(() => {
    toast.value.show = false
    toastTimer = null
  }, duration)
}

function getRowProductId(row) {
  return Number(row?.id ?? row?.productId)
}

function formatNumber(value) {
  const num = parseQuantityValue(value)
  if (num === null || num === 0) return ''
  return num
}

function applyPendingEditPermissions(rows, pendingList) {
  const list = Array.isArray(pendingList) ? pendingList : [];
  return rows.map((row) => {
    const rid = row.productionId;
    const relevant = list.filter((p) => Number(p.productionId) === Number(rid));
    return {
      ...row,
      canEditQuantity: relevant.some((p) => p.quantity === true)
    };
  });
}

function mergeProductsWithProduction(allProducts, savedRows, pendingEditAccess, finalized) {
  const savedMap = new Map(
    (Array.isArray(savedRows) ? savedRows : []).map((row) => [Number(row.id ?? row.productId), row])
  )

  const merged = (Array.isArray(allProducts) ? allProducts : []).map((product) => {
    const productId = Number(product?.id ?? product?.productId)
    const saved = savedMap.get(productId)
    const totalProduced = saved ? Number(saved.quantity || 0) : 0
    const productionId = saved?.productionId ?? null

    return {
      ...product,
      id: productId,
      productId,
      productionId,
      uom: product.uom || saved?.uom || '',
      totalProduced,
      quantity: '',
      canEditQuantity: false
    }
  })

  if (!finalized) {
    return merged.sort((a, b) => String(a.name || a.productName || '').localeCompare(String(b.name || b.productName || '')))
  }

  const withPermissions = applyPendingEditPermissions(
    merged.map((row) => ({
      ...row,
      quantity: row.canEditQuantity ? row.totalProduced : ''
    })),
    pendingEditAccess
  )

  return withPermissions
    .map((row) => ({
      ...row,
      quantity: row.canEditQuantity ? row.totalProduced : '',
      totalProduced: row.totalProduced
    }))
    .sort((a, b) => String(a.name || a.productName || '').localeCompare(String(b.name || b.productName || '')))
}

function inventoryFieldDisabled(row, field) {
  if (isFutureDateSelected()) return true;

  if (isSnapshotLocked.value) {
    if (field === 'quantity') return !row.canEditQuantity;
    return true;
  }
  return false;
}

function isFutureDateSelected() {
  if (!currentDate.value) return false
  return currentDate.value > todayDate.value
}

function parseQuantityValue(value) {
  if (value === '' || value === null || value === undefined) return null
  const qty = parseFloat(String(value).trim())
  return Number.isFinite(qty) ? qty : null
}

function hasPositiveQuantity(row) {
  const qty = parseQuantityValue(row?.quantity)
  return qty !== null && qty > 0
}

function onQuantityInput(row) {
  const raw = String(row?.quantity ?? '')
  if (raw.includes('-')) {
    row.quantity = raw.replace(/-/g, '')
  }
}

function sanitizeQuantity(row) {
  const qty = parseQuantityValue(row?.quantity)
  if (qty === null || qty <= 0) {
    row.quantity = ''
  } else {
    row.quantity = qty
  }
}

function getRowsWithFilledQuantity() {
  return products.value.filter((row) => hasPositiveQuantity(row))
}

const canPartialSaveBatch = computed(() => {
  if (isSaving.value || isPartialSaving.value) return false
  if (!selectedDepartment.value || !currentDate.value) return false
  if (isFutureDateSelected() || isFinalized.value) return false
  return getRowsWithFilledQuantity().length > 0
})

const canSave = computed(() => {
  if (isSaving.value) return false;
  if (!selectedDepartment.value || !currentDate.value) return false;
  if (isFutureDateSelected()) return false;
  if (products.value.length === 0) return false;

  if (isSnapshotLocked.value) {
    const hasPermission = products.value.some(row => row.canEditQuantity);
    if (!hasPermission) return false;
  }
  return true;
});

const saveModal = ref({
  open: false,
  code: '',
  input: '',
  error: ''
})

function generateVerificationCode() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
  let out = ''
  for (let i = 0; i < 4; i++) {
    out += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return out
}

function openSaveModal() {
  if (!canSave.value) return
  saveModal.value.open = true
  saveModal.value.code = generateVerificationCode()
  saveModal.value.input = ''
  saveModal.value.error = ''
}

function closeSaveModal() {
  if (isSaving.value) return
  saveModal.value.open = false
}

async function confirmSave() {
  const expected = String(saveModal.value.code || '').trim()
  const entered = String(saveModal.value.input || '').trim().toUpperCase()
  if (!entered || entered !== expected) {
    saveModal.value.error = 'Invalid code. Please enter the exact 4-character code.'
    return
  }
  saveModal.value.error = ''
  const ok = await saveAllRows()
  if (ok) {
    saveModal.value.open = false
  }
}

const currentUser = computed(() => store.getters.currentUser)
const companyId = computed(() => currentUser.value?.companyId || currentUser.value?.company_id || currentUser.value?.company?.id || null)
const branchId = computed(() => currentUser.value?.branchId || currentUser.value?.branch_id || currentUser.value?.branch?.id || null)

const userDeptList = computed(() => {
  const depts = currentUser.value?.departments || currentUser.value?.department?.name || currentUser.value?.departmentName || currentUser.value?.dept
  if (!depts) return []
  if (typeof depts === 'string') return depts.split(',').map(d => d.trim()).filter(Boolean)
  if (Array.isArray(depts)) return depts.filter(Boolean)
  return [depts].filter(Boolean)
})

const filteredDepartments = computed(() => {
  if (!userDeptList.value.length) return departments.value
  return departments.value.filter(d =>
    userDeptList.value.some(userDept => userDept.toLowerCase() === String(d.name || '').toLowerCase())
  )
})

watch(filteredDepartments, (newVal) => {
  if (newVal.length === 1 && !selectedDepartment.value) {
    selectedDepartment.value = newVal[0].id
    setTimeout(() => { loadProducts() }, 50)
  }
}, { deep: true })

async function fetchDepartments() {
  try {
    const query = branchId.value
      ? `branchId=${branchId.value}`
      : (companyId.value ? `companyId=${companyId.value}` : '')

    const url = query ? `/departments?${query}` : '/departments'

    const res = await apiFetch(url)
    if (!res.ok) return
    const data = await res.json().catch(() => [])

    const raw = Array.isArray(data) ? data : []
    departments.value = raw.filter(d => {
      if (Number(d.status ?? 1) !== 1) return false
      const isCentral = d.isCentral ?? d.is_central
      return isCentral === 0 || isCentral === false
    })
    departments.value.sort((a, b) => a.name.localeCompare(b.name))
  } catch (err) {
    console.error('Error fetching departments:', err)
  }
}

async function fetchAllProductsForDepartment() {
  const dateForProducts = isFutureDateSelected() ? todayDate.value : currentDate.value
  const response = await apiFetch(`/products?departmentId=${selectedDepartment.value}&activeOnly=true&date=${dateForProducts}`)
  if (!response.ok) return []
  const data = await response.json()
  return Array.isArray(data) ? data : []
}

async function fetchSavedProduction() {
  const savedRes = await apiFetch(`/production?departmentId=${selectedDepartment.value}&calendar=${currentDate.value}`)
  if (!savedRes.ok) {
    return { savedRows: [], pendingEditAccess: [], isFinalized: false }
  }

  const savedPayload = await savedRes.json().catch(() => ({ hasData: false, rows: [], pendingEditAccess: [], isFinalized: false }))
  let savedRows = []
  let pendingEditAccess = []
  let finalized = false

  if (Array.isArray(savedPayload)) {
    savedRows = savedPayload
    finalized = savedRows.length > 0
  } else if (savedPayload && savedPayload.rows) {
    savedRows = savedPayload.rows
    pendingEditAccess = savedPayload.pendingEditAccess || []
    finalized = Boolean(savedPayload.isFinalized)
  }

  return { savedRows, pendingEditAccess, isFinalized: finalized }
}

async function loadProducts() {
  if (!selectedDepartment.value || !currentDate.value) return
  loading.value = true
  try {
    if (isFutureDateSelected()) {
      isSnapshotLocked.value = true
      isFinalized.value = true
      const allProducts = await fetchAllProductsForDepartment()
      products.value = allProducts.map(row => ({
        ...row,
        totalProduced: 0,
        quantity: 0
      }))
      products.value.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      await attachLatestSavedProductRecipes(products.value)
      return
    }

    const [allProducts, savedData] = await Promise.all([
      fetchAllProductsForDepartment(),
      fetchSavedProduction()
    ])

    const { savedRows, pendingEditAccess, isFinalized: finalized } = savedData
    isFinalized.value = finalized
    isSnapshotLocked.value = finalized

    products.value = mergeProductsWithProduction(allProducts, savedRows, pendingEditAccess, finalized)
    await attachLatestSavedProductRecipes(products.value)
  } catch (err) {
    console.error('Error loading products:', err)
  } finally {
    loading.value = false
  }
}

function normalizeIngredientAndAmount(raw) {
  if (!Array.isArray(raw)) return null
  const out = raw.map(entry => ({
    Id: Number(entry?.Id ?? entry?.id),
    type: entry?.type,
    amount: Number(entry?.amount)
  })).filter(e => Number.isFinite(e.Id) && e.Id > 0 && (e.type === 'Item' || e.type === 'Product') && Number.isFinite(e.amount))
  return out.length ? out : null
}

function buildSaveItem(row, quantity) {
  const parsedQty = parseQuantityValue(quantity)
  return {
    ...row,
    id: getRowProductId(row),
    quantity: parsedQty !== null ? parsedQty : 0,
    costPerUnit: Number(row.costPerUnit ?? row.unitCost ?? 0),
    ingredient_and_amount: normalizeIngredientAndAmount(
      row?.latestSavedProductRecipe?.ingredientAndAmount ??
      row?.latestSavedProductRecipe?.ingredient_and_amount ??
      null
    )
  }
}

async function attachLatestSavedProductRecipes(rows) {
  const list = Array.isArray(rows) ? rows : []
  const productIds = [...new Set(list.map(r => Number(r?.id ?? r?.productId)).filter(n => Number.isFinite(n) && n > 0))]
  if (!productIds.length) return

  recipeSnapshotsLoading.value = true
  try {
    const res = await apiFetch('/product-recipes/saved-product-recipe/latest-batch', {
      method: 'POST',
      body: JSON.stringify({ productIds })
    })
    const payload = res.ok ? await res.json().catch(() => null) : null
    const recipes = payload?.recipes && typeof payload.recipes === 'object' ? payload.recipes : {}

    products.value = list.map(row => {
      const pid = Number(row?.id ?? row?.productId)
      const data = recipes[pid] ?? recipes[String(pid)] ?? null
      if (!data) {
        return { ...row, latestSavedProductRecipe: null }
      }
      const ingredientAndAmount = normalizeIngredientAndAmount(
        data.ingredientAndAmount ?? data.ingredient_and_amount ?? null
      )
      return {
        ...row,
        latestSavedProductRecipe: { ...data, ingredientAndAmount }
      }
    })
  } catch (err) {
    console.error('Error attaching latest saved product recipes:', err)
    products.value = list.map(row => ({
      ...row,
      latestSavedProductRecipe: row.latestSavedProductRecipe ?? null
    }))
  } finally {
    recipeSnapshotsLoading.value = false
  }
}

async function savePartialBatch() {
  if (!canPartialSaveBatch.value) return

  const rowsToSave = getRowsWithFilledQuantity()
  if (!rowsToSave.length) {
    showToast('Enter a quantity greater than 0 for at least one product.', 'error')
    return
  }

  isPartialSaving.value = true
  try {
    const res = await apiFetch('/production/batch', {
      method: 'POST',
      body: JSON.stringify({
        date: currentDate.value,
        calendar: currentDate.value,
        departmentId: selectedDepartment.value,
        saveMode: 'partial',
        items: rowsToSave.map((row) => buildSaveItem(row, row.quantity))
      })
    })

    if (res.ok) {
      const savedCount = rowsToSave.length
      rowsToSave.forEach((row) => {
        const addQty = parseQuantityValue(row.quantity) || 0
        row.totalProduced = Number(row.totalProduced || 0) + addQty
        row.quantity = ''
      })
      showToast(
        savedCount === 1
          ? 'Product quantity saved successfully.'
          : `${savedCount} product quantities saved successfully.`,
        'success'
      )
      return
    }

    const err = await res.json().catch(() => ({}))
    showToast(err.message || 'Failed to save product quantities.', 'error')
  } catch (err) {
    console.error('Error saving partial batch:', err)
    showToast('Failed to save product quantities.', 'error')
  } finally {
    isPartialSaving.value = false
  }
}

async function saveAllRows() {
  if (!canSave.value) return false

  const itemsToSave = products.value

  if (itemsToSave.length === 0) {
    alert('No products to save.')
    return false
  }

  isSaving.value = true
  try {
    const res = await apiFetch('/production/batch', {
      method: 'POST',
      body: JSON.stringify({
        date: currentDate.value,
        calendar: currentDate.value,
        departmentId: selectedDepartment.value,
        saveMode: 'final',
        items: itemsToSave.map((row) => {
          let totalQty
          if (isFinalized.value) {
            totalQty = parseQuantityValue(row?.quantity) ?? Number(row.totalProduced || 0)
          } else {
            const inputQty = parseQuantityValue(row?.quantity) ?? 0
            totalQty = Number(row.totalProduced || 0) + inputQty
          }
          return buildSaveItem(row, totalQty)
        })
      })
    })

    if (res.ok) {
      alert('Product saved successfully!')
      selectedDepartment.value = ''
      products.value = []
      isFinalized.value = false
      isSnapshotLocked.value = false
      return true
    } else {
      const err = await res.json()
      alert('Error saving: ' + (err.message || 'Unknown error'))
      return false
    }
  } catch (err) {
    console.error('Error saving product:', err)
    alert('Failed to save product.')
    return false
  } finally {
    isSaving.value = false
  }
}

onMounted(() => {
  currentDate.value = ""
  fetchDepartments()
})
</script>

<style scoped>
.daily-product-table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 0.75rem;
  color: #1e293b;
  border-collapse: collapse;
}

th, td {
  padding: 8px 10px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
}

th {
  background: #f1f5f9;
  cursor: default;
  font-weight: 600;
  position: sticky;
  top: 0;
  z-index: 20;
}

.custom-scrollbar::-webkit-scrollbar { width: 8px; height: 8px; }
.custom-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
.custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }

.spinner {
  width: 14px;
  height: 14px;
  border: 2px solid #ffffff;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.toast-enter-active,
.toast-leave-active {
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}
.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateX(30px);
}
</style>
